<script setup>
/* eslint-disable */
import { onMounted, onBeforeUnmount, watch, ref, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import _ from "lodash";

import { useBetBoosterStore } from "@/stores/bet_booster";
import { usePreSportsStore } from "@/stores/pre_sports";
import { useMarketsStore } from "@/stores/markets";
import { useFavouritesStore } from "@/stores/favourites";
import { useSocketStore } from "@/stores/socket";
import { useDatesStore } from "@/stores/dates";

import PreFixtureDoubleMarket from "@/components/pre_sports/highlights/PreFixtureDoubleMarket.vue";
import FixtureOrderSelector from "@/components/pre_sports/highlights/FixtureOrderSelector.vue";
import FixtureSkeletonLoader from "@/components/pre_sports/highlights/FixtureSkeletonLoader.vue";

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const betBoosterStore = useBetBoosterStore();
const preSportsStore = usePreSportsStore();
const marketsStore = useMarketsStore();
const favouritesStore = useFavouritesStore();
const socketStore = useSocketStore();
const datesStore = useDatesStore();

const selected_fixture_order = ref("popular");

const primary_overview_market = ref(null);
const secondary_overview_market = ref(null);
const show_primary_overview_market = ref(false);
const show_secondary_overview_market = ref(false);
const collapsedCompetitions = ref({});
const collapsedDates = ref({});

const joined_fixture_ids = new Set();

const currentSportId = computed(() => {
	const param = route.params.sport_id;
	if (!param || param === "all") return 1;
	const num = Number(param);
	return isNaN(num) ? 1 : num;
});

const isAllSportsSelected = computed(() => {
	return !route.params.sport_id || route.params.sport_id === "all";
});

// Update overview markets when sport changes
const updateOverviewMarkets = () => {
	const sportId = currentSportId.value;
	const sport_overview_markets = marketsStore.sportOverviewMarkets(sportId);

	if (sport_overview_markets && sport_overview_markets.length) {
		if (sport_overview_markets.length > 1) {
			primary_overview_market.value = sport_overview_markets[0];
			secondary_overview_market.value = sport_overview_markets[1];
		} else {
			primary_overview_market.value = sport_overview_markets[0];
			secondary_overview_market.value = sport_overview_markets[0];
		}
	} else if (!marketsStore.overview_markets || Object.keys(marketsStore.overview_markets).length === 0) {
		marketsStore.fetchOverviewMarkets().then(() => {
			const loadedMarkets = marketsStore.sportOverviewMarkets(sportId);
			if (loadedMarkets && loadedMarkets.length) {
				primary_overview_market.value = loadedMarkets[0];
				secondary_overview_market.value = loadedMarkets.length > 1 ? loadedMarkets[1] : loadedMarkets[0];
			}
		}).catch(() => {});
	}
};

const toggleCompetitionCollapse = (competitionId) => {
	const key = String(competitionId);
	collapsedCompetitions.value = {
		...collapsedCompetitions.value,
		[key]: !collapsedCompetitions.value[key]
	};
};

const isCompetitionClosed = (competitionId) => {
	return !!collapsedCompetitions.value[String(competitionId)];
};

const toggleDateCollapse = (dateStr) => {
	const key = String(dateStr);
	collapsedDates.value = {
		...collapsedDates.value,
		[key]: !collapsedDates.value[key]
	};
};

const isDateClosed = (dateStr) => {
	return !!collapsedDates.value[String(dateStr)];
};

const closePrimaryOverviewMarketSelector = () => {
	show_primary_overview_market.value = false;
};

const closeSecondaryOverviewMarketSelector = () => {
	show_secondary_overview_market.value = false;
};

const selectPrimaryOverviewMarket = (overview_market) => {
	primary_overview_market.value = overview_market;
	show_primary_overview_market.value = false;
};

const selectSecondaryOverviewMarket = (overview_market) => {
	secondary_overview_market.value = overview_market;
	show_secondary_overview_market.value = false;
};

// Sockets management
const syncSocketSubscriptions = () => {
	const fixtures = betBoosterStore.bet_boosted_fixtures || [];
	fixtures.forEach((item) => {
		const fId = item.fixture_id;
		if (fId && !joined_fixture_ids.has(fId)) {
			socketStore.join("favouriteFixture", fId);
			joined_fixture_ids.add(fId);
		}
	});
};

const leaveAllBoostedSockets = () => {
	joined_fixture_ids.forEach((fId) => {
		socketStore.leave("favouriteFixture", fId);
	});
	joined_fixture_ids.clear();
};

// Filtered boosted bets
const displayedBoosts = computed(() => {
	let list = betBoosterStore.filteredBoostedBets || [];

	if (betBoosterStore.selected_competition_id) {
		list = list.filter(
			(item) => String(item.competition_id) === String(betBoosterStore.selected_competition_id)
		);
	}

	const validItems = list.filter((item) => item.fixture && !item.fixture.live);
	return _.uniqBy(validItems, "fixture_id");
});

// Chronological sorting grouped by date (Time First - Option A)
const timeSortedBoostsByDate = computed(() => {
	const sorted = _.orderBy(displayedBoosts.value, ["start_datetime"], ["asc"]);
	const byDate = {};

	sorted.forEach((item) => {
		const dateStr = item.start_datetime
			? datesStore.competitionFixtureDate(item.start_datetime)
			: "Upcoming";
		if (!byDate[dateStr]) {
			byDate[dateStr] = [];
		}
		byDate[dateStr].push(item);
	});

	return byDate;
});

// Grouped by competition & date (Popular First - Option B)
const competitionGroupedBoosts = computed(() => {
	const groupsMap = new Map();

	displayedBoosts.value.forEach((item) => {
		const compId = item.competition_id || 0;
		if (!groupsMap.has(compId)) {
			groupsMap.set(compId, {
				id: compId,
				competition_id: compId,
				name: item.competition_name || item.competition || "Competition",
				region_name: item.fixture?.region_name || "",
				region_icon: item.fixture?.region_icon || item.fixture?.region_icon_url || null,
				sport_id: item.sport_id,
				items: []
			});
		}
		groupsMap.get(compId).items.push(item);
	});

	const groups = Array.from(groupsMap.values());
	groups.forEach((group) => {
		const sortedItems = _.orderBy(group.items, ["start_datetime"], ["asc"]);
		const byDate = {};

		sortedItems.forEach((item) => {
			const dateStr = item.start_datetime
				? datesStore.competitionFixtureDate(item.start_datetime)
				: "Upcoming";
			if (!byDate[dateStr]) {
				byDate[dateStr] = [];
			}
			byDate[dateStr].push(item);
		});

		group.dates = byDate;
	});

	return groups;
});

const currentSportName = computed(() => {
	if (isAllSportsSelected.value) {
		return t("general.all", "All");
	}
	const sport = _.find(preSportsStore.preSports, { id: Number(route.params.sport_id) });
	return sport ? sport.name : (preSportsStore.preSportName ? preSportsStore.preSportName(route.params.sport_id) : "");
});

const resetToAllSports = () => {
	betBoosterStore.setSelectedSport("all");
	betBoosterStore.setSelectedCompetition(null);
	router.push({ name: "pre-sports-bet-boosts", params: { sport_id: "all" } });
};

const resetCompetitionFilter = () => {
	betBoosterStore.setSelectedCompetition(null);
};

const goToHighlights = () => {
	router.push({ name: "pre-sports-highlights", params: { sport_id: currentSportId.value } });
};

const isInitialLoading = ref(displayedBoosts.value.length === 0);
let loadingTimeout = null;

const checkAllFixturesLoaded = () => {
	if (displayedBoosts.value.length > 0) {
		isInitialLoading.value = false;
		if (loadingTimeout) {
			clearTimeout(loadingTimeout);
			loadingTimeout = null;
		}
		return;
	}

	const configs = betBoosterStore.bet_boosted_fixtures;
	if (configs && configs.length > 0) {
		const uniqueIds = new Set(configs.map((c) => c.fixture_id));
		let receivedCount = 0;
		for (const id of uniqueIds) {
			if (betBoosterStore.fixtures_data && betBoosterStore.fixtures_data[id]) {
				receivedCount++;
			}
		}
		if (receivedCount >= uniqueIds.size) {
			isInitialLoading.value = false;
			if (loadingTimeout) {
				clearTimeout(loadingTimeout);
				loadingTimeout = null;
			}
		}
	}
};

const isLoading = computed(() => {
	return betBoosterStore.loading || isInitialLoading.value;
});

// Lifecycle
onMounted(() => {
	const initial_sport = (!route.params.sport_id || route.params.sport_id === "all") ? 1 : route.params.sport_id;
	betBoosterStore.setSelectedSport(initial_sport);
	if (!route.params.sport_id || route.params.sport_id === "all") {
		router.replace({ name: "pre-sports-bet-boosts", params: { sport_id: initial_sport } });
	}

	betBoosterStore.startExpirationTimer();

	// If boosted fixtures are already available in store, subscribe immediately
	if (betBoosterStore.bet_boosted_fixtures?.length) {
		syncSocketSubscriptions();
	}

	// Safety fallback timeout: ensure skeleton clears within 2 seconds if no boosts exist
	if (loadingTimeout) clearTimeout(loadingTimeout);
	loadingTimeout = setTimeout(() => {
		isInitialLoading.value = false;
	}, 2000);

	betBoosterStore.fetchBoostedBets().then(() => {
		syncSocketSubscriptions();
		if (!betBoosterStore.bet_boosted_fixtures || betBoosterStore.bet_boosted_fixtures.length === 0) {
			isInitialLoading.value = false;
			if (loadingTimeout) {
				clearTimeout(loadingTimeout);
				loadingTimeout = null;
			}
		}
	}).catch(() => {
		isInitialLoading.value = false;
		if (loadingTimeout) {
			clearTimeout(loadingTimeout);
			loadingTimeout = null;
		}
	});

	updateOverviewMarkets();
});

onBeforeUnmount(() => {
	if (loadingTimeout) {
		clearTimeout(loadingTimeout);
		loadingTimeout = null;
	}
	leaveAllBoostedSockets();
	betBoosterStore.stopExpirationTimer();
	betBoosterStore.setSelectedCompetition(null);
});

watch(
	() => displayedBoosts.value.length,
	(count) => {
		if (count > 0) {
			isInitialLoading.value = false;
			if (loadingTimeout) {
				clearTimeout(loadingTimeout);
				loadingTimeout = null;
			}
		}
	},
	{ immediate: true }
);

watch(
	() => betBoosterStore.fixtures_data,
	() => {
		checkAllFixturesLoaded();
	},
	{ deep: true }
);

watch(
	() => betBoosterStore.bet_boosted_fixtures,
	() => {
		syncSocketSubscriptions();
	},
	{ deep: true }
);

watch(
	() => route.params.sport_id,
	(newSportId) => {
		const target = newSportId || "all";
		betBoosterStore.setSelectedSport(target);
		betBoosterStore.setSelectedCompetition(null);
		updateOverviewMarkets();
	}
);

watch(
	() => marketsStore.overview_markets,
	() => {
		updateOverviewMarkets();
	}
);
</script>

<template>
	<div class="pre-sports-bet-boosts-container">
		<!-- Main Highlights / Fixtures Dashboard (Matching Pre-Sports live-bet-dashboard structure) -->
		<div class="live-bet-dashboard round-games-main set_game-outer bet-boosts-top-dashboard" v-if="displayedBoosts.length > 0">
			<!-- Top Controls Bar -->
			<div class="bet-dashboard-top">
				<div class="tabing-dashboard">
					<div class="tab-pills">
						<button class="tab-link active boost-tab-pill" type="button" style="background: transparent !important;">
							<span class="boost-rocket-badge">🚀</span>
							<span class="boost-page-title">{{ $t('bet_boost.title', 'Bet Boosts') }}</span>
							<span class="boost-total-count">
								{{ displayedBoosts.length }}
							</span>
						</button>
					</div>

					<!-- <div
						v-if="betBoosterStore.selected_competition_id"
						class="active-competition-chip"
						@click="resetCompetitionFilter"
						title="Click to clear filter"
					>
						<span>{{ $t('general.filtered_by', 'Filtered') }}</span>
						<button class="chip-remove-btn" type="button">✕</button>
					</div> -->
				</div>

				<!-- Primary Overview Market Dropdown -->
				<div class="dashboard-info-label dropdown-option-top" v-if="primary_overview_market">
					<div class="select-option">
						<dl class="drop" :class="show_primary_overview_market ? 'active' : ''">
							<span class="tabIco">
								<svg xmlns="http://www.w3.org/2000/svg" width="4" height="3" viewBox="0 0 4 3">
									<g id="Group_73" data-name="Group 73" transform="translate(-134 -568)">
										<path id="Polygon_2" data-name="Polygon 2" d="M2,0,4,3H0Z" transform="translate(138 571) rotate(180)" fill="var(--white)" />
									</g>
								</svg>
							</span>
							<dt style="display: flex; align-items: center; justify-content: space-between; padding-right: 15px;">
								<button class="tablinks" style="flex-grow: 1; text-align: left;" type="button" @click="show_primary_overview_market = !show_primary_overview_market" v-click-outside-element="closePrimaryOverviewMarketSelector">
									{{ primary_overview_market?.name?.toUpperCase() }}
								</button>
							</dt>
							<dd>
								<ul>
									<li
										v-for="overview_market in marketsStore.sportOverviewMarkets(currentSportId)"
										:key="overview_market.slug"
										@click="selectPrimaryOverviewMarket(overview_market)"
										:class="primary_overview_market?.slug === overview_market.slug ? 'active' : ''"
										style="display: flex; justify-content: space-between; align-items: center; padding-right: 15px;"
									>
										<button class="tablinks" style="flex-grow: 1; text-align: left;" type="button">
											{{ overview_market?.name?.toUpperCase() }}
										</button>
										<div
											class="live-fav-icon"
											style="cursor: pointer; padding-left: 10px; display: flex; align-items: center;"
											@click.stop="favouritesStore.toggleFavouriteOverviewMarket(currentSportId, overview_market)"
										>
											<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 12 11.143">
												<path
													:style="{ fill: favouritesStore.isOverviewFavourite(currentSportId, overview_market) ? 'var(--primary)' : 'var(--tab-gray)' }"
													id="Icon_ionic-ios-star"
													data-name="Icon ionic-ios-star"
													d="M12.67,5.545H8.729l-1.2-3.573a.434.434,0,0,0-.814,0l-1.2,3.573H1.554a.43.43,0,0,0-.429.429.315.315,0,0,0,.008.072.412.412,0,0,0,.179.3L4.551,8.63,3.308,12.244a.43.43,0,0,0,.147.482.414.414,0,0,0,.241.1.525.525,0,0,0,.268-.1l3.161-2.253,3.161,2.253a.5.5,0,0,0,.268.1.385.385,0,0,0,.238-.1.425.425,0,0,0,.147-.482L9.7,8.63l3.212-2.3.078-.067a.449.449,0,0,0,.139-.287A.454.454,0,0,0,12.67,5.545Z"
													transform="translate(-1.125 -1.688)"
												/>
											</svg>
										</div>
									</li>
								</ul>
							</dd>
						</dl>
					</div>
				</div>

				<!-- Secondary Overview Market Dropdown -->
				<div class="dashboard-info-label dropdown-option-top dropdown-option-top-2" v-if="secondary_overview_market">
					<div class="select-option">
						<dl class="drop" :class="show_secondary_overview_market ? 'active' : ''">
							<span class="tabIco">
								<svg xmlns="http://www.w3.org/2000/svg" width="4" height="3" viewBox="0 0 4 3">
									<g id="Group_73" data-name="Group 73" transform="translate(-134 -568)">
										<path id="Polygon_2" data-name="Polygon 2" d="M2,0,4,3H0Z" transform="translate(138 571) rotate(180)" fill="var(--white)" />
									</g>
								</svg>
							</span>
							<dt style="display: flex; align-items: center; justify-content: space-between; padding-right: 15px;">
								<button class="tablinks" style="flex-grow: 1; text-align: left;" type="button" @click="show_secondary_overview_market = !show_secondary_overview_market" v-click-outside-element="closeSecondaryOverviewMarketSelector">
									{{ secondary_overview_market?.name?.toUpperCase() }}
								</button>
							</dt>
							<dd>
								<ul>
									<li
										v-for="overview_market in marketsStore.sportOverviewMarkets(currentSportId)"
										:key="overview_market.slug"
										@click="selectSecondaryOverviewMarket(overview_market)"
										:class="secondary_overview_market?.slug === overview_market.slug ? 'active' : ''"
										style="display: flex; justify-content: space-between; align-items: center; padding-right: 15px;"
									>
										<button class="tablinks" style="flex-grow: 1; text-align: left;" type="button">
											{{ overview_market?.name?.toUpperCase() }}
										</button>
										<div
											class="live-fav-icon"
											style="cursor: pointer; padding-left: 10px; display: flex; align-items: center;"
											@click.stop="favouritesStore.toggleFavouriteOverviewMarket(currentSportId, overview_market)"
										>
											<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 12 11.143">
												<path
													:style="{ fill: favouritesStore.isOverviewFavourite(currentSportId, overview_market) ? 'var(--primary)' : 'var(--tab-gray)' }"
													id="Icon_ionic-ios-star"
													data-name="Icon ionic-ios-star"
													d="M12.67,5.545H8.729l-1.2-3.573a.434.434,0,0,0-.814,0l-1.2,3.573H1.554a.43.43,0,0,0-.429.429.315.315,0,0,0,.008.072.412.412,0,0,0,.179.3L4.551,8.63,3.308,12.244a.43.43,0,0,0,.147.482.414.414,0,0,0,.241.1.525.525,0,0,0,.268-.1l3.161-2.253,3.161,2.253a.5.5,0,0,0,.268.1.385.385,0,0,0,.238-.1.425.425,0,0,0,.147-.482L9.7,8.63l3.212-2.3.078-.067a.449.449,0,0,0,.139-.287A.454.454,0,0,0,12.67,5.545Z"
													transform="translate(-1.125 -1.688)"
												/>
											</svg>
										</div>
									</li>
								</ul>
							</dd>
						</dl>
					</div>
				</div>

				<FixtureOrderSelector v-model="selected_fixture_order" />
			</div>

			<!-- Live Tab Info Container -->
			<div class="live-tab-info" id="tabs">
				<!-- OPTION B: Popular / Competition First -->
				<div id="popular" class="tab-content" v-if="selected_fixture_order === 'popular'">
					<div
						class="live-info-outer"
						v-for="(competition, compIndex) in competitionGroupedBoosts"
						:key="competition.id"
						:class="{ 'is-closed': isCompetitionClosed(competition.id), 'is-open': !isCompetitionClosed(competition.id) }"
					>
						<!-- Native Competition Header (Matches highlights/CompetitionFixtures.vue) -->
						<div
							class="live-info-inner competition-header-row"
							:class="{ 'not-first-competition': compIndex > 0 }"
							@click="toggleCompetitionCollapse(competition.id)"
						>
							<div class="live-info-team">
								<div class="live-match">
									<div class="country-flag team-flag" v-if="competition.region_icon">
										<img :src="'/assets' + competition.region_icon" alt="country-flag">
									</div>
									<div class="live-match-name">
										<h3>
											{{ competition.region_name ? competition.region_name + ' - ' : '' }}{{ competition.name }}
										</h3>
									</div>
								</div>
							</div>

							<!-- Mobile collapse arrow button & count (aligned on the right on mobile) -->
							<div class="takis-gkonias market-arrow-mobile" :title="isCompetitionClosed(competition.id) ? 'Expand' : 'Collapse'">
								<span>({{ competition.items.length }})</span>
								<div class="icon" :class="{'rotated': !isCompetitionClosed(competition.id)}"></div>
							</div>

							<div
								class="full-time-top top-title-dash"
								:class="{
									'totals_new-grid': primary_overview_market?.market_type == 'totals_new',
									'handicap_new_grid': primary_overview_market?.market_type == 'handicap_new'
								}"
							>
								<span v-if="primary_overview_market?.market_type == 'totals_new' || primary_overview_market?.market_type == 'handicap_new'"/>
								<template v-for="(odd, id) in primary_overview_market?.odd_names" :key="id">
									<span v-if="odd?.length >= 1">{{ odd }}</span>
								</template>
							</div>

							<div
								class="match-goal-top top-title-dash"
								:class="{
									'totals_new-grid': secondary_overview_market?.market_type == 'totals_new',
									'handicap_new_grid': secondary_overview_market?.market_type == 'handicap_new'
								}"
							>
								<span v-if="secondary_overview_market?.market_type == 'totals_new' || secondary_overview_market?.market_type == 'handicap_new'"/>
								<template v-for="(odd, id) in secondary_overview_market?.odd_names" :key="id">
									<span v-if="odd?.length >= 1">{{ odd }}</span>
								</template>
								<span class="bet-column-empty">
									<div class="market-arrow" :title="isCompetitionClosed(competition.id) ? 'Expand' : 'Collapse'">
										<a class="market-arrow-icon" :class="isCompetitionClosed(competition.id) ? 'market-arrow-icon-closed' : ''"></a>
									</div>
								</span>
							</div>
						</div>

						<!-- Fixtures under this competition -->
						<template v-if="!isCompetitionClosed(competition.id)">
							<template v-for="(dateGroup, dateStr) in competition.dates" :key="dateStr">
								<!-- Date Header -->
								<div class="live-info-inner date-sub-header">
									<div class="live-info-team">
										<div class="live-match competetion-date">
											<div class="live-match-name">
												<h3>{{ dateStr }}</h3>
											</div>
										</div>
									</div>
								</div>

								<!-- Fixtures under this date -->
								<template v-for="item in dateGroup" :key="item.fixture_id">
									<PreFixtureDoubleMarket
										v-if="item.fixture"
										:primary_overview_market="primary_overview_market"
										:secondary_overview_market="secondary_overview_market"
										:fixture="item.fixture"
										:show_competition_region="false"
									/>
								</template>
							</template>
						</template>
					</div>
				</div>

				<!-- OPTION A: Time First (Chronological Feed by Date) -->
				<div id="by-time" class="tab-content" v-else-if="selected_fixture_order === 'time'">
					<div
						class="live-info-outer"
						v-for="(dateGroup, dateStr, dateIndex) in timeSortedBoostsByDate"
						:key="dateStr"
						:class="{ 'is-closed': isDateClosed(dateStr), 'is-open': !isDateClosed(dateStr) }"
					>
						<!-- Date Header with Odds Columns -->
						<div class="live-info-inner date-header-row" @click="toggleDateCollapse(dateStr)">
							<div class="live-info-team">
								<div class="live-match competetion-date">
									<div class="live-match-name">
										<h3>{{ dateStr }}</h3>
									</div>
								</div>
							</div>

							<!-- Mobile collapse arrow button & count (aligned on the right on mobile) -->
							<div class="takis-gkonias market-arrow-mobile" :title="isDateClosed(dateStr) ? 'Expand' : 'Collapse'">
								<span>({{ dateGroup.length }})</span>
								<div class="icon" :class="{'rotated': !isDateClosed(dateStr)}"></div>
							</div>

							<div
								class="full-time-top top-title-dash"
								:class="{
									'totals_new-grid': primary_overview_market?.market_type == 'totals_new',
									'handicap_new_grid': primary_overview_market?.market_type == 'handicap_new'
								}"
							>
								<span v-if="primary_overview_market?.market_type == 'totals_new' || primary_overview_market?.market_type == 'handicap_new'"/>
								<template v-for="(odd, id) in primary_overview_market?.odd_names" :key="id">
									<span v-if="odd?.length >= 1">{{ odd }}</span>
								</template>
							</div>

							<div
								class="match-goal-top top-title-dash"
								:class="{
									'totals_new-grid': secondary_overview_market?.market_type == 'totals_new',
									'handicap_new_grid': secondary_overview_market?.market_type == 'handicap_new'
								}"
							>
								<span v-if="secondary_overview_market?.market_type == 'totals_new' || secondary_overview_market?.market_type == 'handicap_new'"/>
								<template v-for="(odd, id) in secondary_overview_market?.odd_names" :key="id">
									<span v-if="odd?.length >= 1">{{ odd }}</span>
								</template>
								<span class="bet-column-empty">
									<div class="market-arrow" :title="isDateClosed(dateStr) ? 'Expand' : 'Collapse'">
										<a class="market-arrow-icon" :class="isDateClosed(dateStr) ? 'market-arrow-icon-closed' : ''"></a>
									</div>
								</span>
							</div>
						</div>

						<!-- Fixtures in chronological order -->
						<template v-if="!isDateClosed(dateStr)">
							<template v-for="item in dateGroup" :key="item.fixture_id">
								<PreFixtureDoubleMarket
									v-if="item.fixture"
									:primary_overview_market="primary_overview_market"
									:secondary_overview_market="secondary_overview_market"
									:fixture="item.fixture"
									:show_competition_region="true"
								/>
							</template>
						</template>
					</div>
				</div>
			</div>
		</div>

		<!-- Skeleton Loader -->
		<div v-else-if="isLoading && displayedBoosts.length === 0" class="boost-skeleton-wrapper">
			<FixtureSkeletonLoader :rows="6" />
		</div>

		<!-- Empty State (Keeps the Top Sport Selector Intact!) -->
		<div v-else class="empty-state-card">
			<div class="empty-state-rocket-wrapper">
				<div class="rocket-glow"></div>
				<span class="empty-state-rocket-emoji">🚀</span>
			</div>

			<h3 class="empty-state-heading">
				{{
					betBoosterStore.selected_competition_id
						? $t('bet_boost.no_boosts_for_league', 'No Bet Boosts in this league')
						: isAllSportsSelected
						? $t('bet_boost.no_boosts_available', 'No Active Bet Boosts Right Now')
						: $t('bet_boost.no_boosts_for_sport', 'No Bet Boosts for {sport}', { sport: currentSportName })
				}}
			</h3>

			<p class="empty-state-subheading">
				{{
					betBoosterStore.selected_competition_id
						? $t('bet_boost.league_empty_desc', 'There are no active boosted bets for this specific competition. Try viewing all leagues or checking another sport.')
						: isAllSportsSelected
						? $t('bet_boost.all_empty_desc', 'Our boosted odds are refreshed regularly before big matches. Check back shortly or explore today\'s top events!')
						: $t('bet_boost.sport_empty_desc', 'Currently there are no boosted bets available for {sport}. Switch to All Sports or explore other active markets.', { sport: currentSportName })
				}}
			</p>

			<div class="empty-state-actions">
				<button
					v-if="betBoosterStore.selected_competition_id"
					class="empty-action-btn primary"
					@click="resetCompetitionFilter"
				>
					{{ $t('bet_boost.view_all_leagues', 'View All Leagues') }}
				</button>

				<!-- <button
					v-else-if="!isAllSportsSelected"
					class="empty-action-btn primary"
					@click="resetToAllSports"
				>
					{{ $t('bet_boost.view_all_sports', 'View All Bet Boosts') }}
				</button> -->

				<button
					class="empty-action-btn"
					:class="betBoosterStore.selected_competition_id ? 'secondary' : 'primary'"
					@click="goToHighlights"
				>
					{{ $t('bet_boost.explore_highlights', 'Explore Sports Highlights') }}
				</button>
			</div>
		</div>
	</div>
</template>

<style scoped>
.pre-sports-bet-boosts-container {
	width: 100%;
	padding-top: 28px;
}

.bet-boosts-header-bar {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding-bottom: 14px;
	margin-bottom: 22px;
	border-bottom: 1px solid var(--tab-gray, rgba(255, 255, 255, 0.12));
	background: transparent;
	position: relative;
}

.bet-boosts-header-bar .header-left {
	display: flex;
	align-items: center;
	gap: 12px;
}

.boost-tab-pill {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 6px 10px !important;
	color: var(--white) !important;
	border-radius: 4px !important;
	font-weight: 600 !important;
	font-size: 13px !important;
	letter-spacing: 0.3px;
	border: none;
	cursor: default;
	text-transform: uppercase;
	transition: all 0.2s ease;
	white-space: nowrap;
}

.boost-tab-pill:after,
.boost-tab-pill.active:after {
	display: none !important;
	content: none !important;
}

.boost-rocket-badge {
	font-size: 14px;
	line-height: 1;
	flex-shrink: 0;
}

.boost-page-title {
	font-size: 13px;
	font-weight: 700;
	margin: 0;
	color: inherit;
	white-space: nowrap;
}

.boost-total-count {
	font-size: 11px;
	font-weight: 800;
	padding: 1px 6px;
	border-radius: 10px;
	background: rgba(0, 0, 0, 0.25);
	color: var(--white);
	margin-left: 2px;
	flex-shrink: 0;
}

.active-competition-chip {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 5px 10px;
	border-radius: 4px;
	background: rgba(var(--primary-rgb, 99, 162, 70), 0.15);
	border: 1px solid rgba(var(--primary-rgb, 99, 162, 70), 0.4);
	color: var(--primary);
	font-size: 12px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.2s ease;
}

.active-competition-chip:hover {
	background: rgba(var(--primary-rgb, 99, 162, 70), 0.25);
}

.chip-remove-btn {
	background: transparent;
	border: none;
	color: inherit;
	cursor: pointer;
	font-size: 11px;
	padding: 0;
	line-height: 1;
}


/* Empty State Card */
.empty-state-card {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 56px 24px;
	text-align: center;
	background: var(--light-dark);
	border-radius: 10px;
	/* border: 1px dashed rgba(255, 255, 255, 0.12); */
	/* margin-top: 8px; */
	animation: fadeIn 0.35s ease forwards;
}

@keyframes fadeIn {
	from {
		opacity: 0;
		transform: translateY(6px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

.empty-state-rocket-wrapper {
	position: relative;
	width: 72px;
	height: 72px;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-bottom: 20px;
}

.rocket-glow {
	position: absolute;
	width: 100%;
	height: 100%;
	border-radius: 50%;
	background: radial-gradient(circle, rgba(245, 158, 11, 0.22) 0%, rgba(99, 162, 70, 0.08) 60%, transparent 80%);
	filter: blur(8px);
}

.empty-state-rocket-emoji {
	font-size: 38px;
	line-height: 1;
	position: relative;
	z-index: 1;
}

.empty-state-heading {
	font-size: 1.15rem;
	font-weight: 700;
	color: var(--white);
	margin: 0 0 8px 0;
}

.empty-state-subheading {
	font-size: 0.875rem;
	color: var(--light-gray);
	margin: 0 0 24px 0;
	max-width: 440px;
	line-height: 1.55;
}

.empty-state-actions {
	display: flex;
	gap: 12px;
	flex-wrap: wrap;
	justify-content: center;
}

.empty-action-btn {
	padding: 9px 18px;
	border-radius: 6px;
	font-size: 12px;
	font-weight: 700;
	cursor: pointer;
	transition: all 0.2s ease;
	border: none;
}

.empty-action-btn.primary {
	background: var(--primary);
	color: var(--white);
}

.empty-action-btn.primary:hover {
	filter: brightness(1.1);
	transform: translateY(-1px);
}

.empty-action-btn.secondary {
	background: rgba(255, 255, 255, 0.08);
	color: var(--white);
}

.empty-action-btn.secondary:hover {
	background: rgba(255, 255, 255, 0.14);
}

.boost-skeleton-wrapper {
	padding: 12px 0;
}

@media screen and (max-width: 767px) {
	.bet-boosts-header-bar {
		flex-direction: column;
		align-items: stretch;
		gap: 10px;
	}

	.empty-state-card {
		padding: 40px 16px;
	}

	.empty-state-actions {
		flex-direction: column;
		width: 100%;
	}

	.empty-action-btn {
		width: 100%;
	}
}

/* Market dropdown styling matching pre-sports */
.bet-boosts-top-dashboard {
	padding-top: 0px !important;
	margin-bottom: 14px;
}

.live-bet-dashboard .dashboard-info-label .select-option .drop dd ul {
	min-width: 120px;
}

.live-bet-dashboard.round-games-main .bet-dashboard-top {
	grid-template-columns: 30% 24% 24% 22%;
}

.live-bet-dashboard.round-games-main .bet-dashboard-top:after {
	width: 100% !important;
}

.live-bet-dashboard .bet-dashboard-top .select-option .drop.active dd {
	display: block !important;
}

.live-bet-dashboard .bet-dashboard-top .select-option .drop.active dd ul {
	z-index: 1000 !important;
}

.live-bet-dashboard .bet-dashboard-top .dropdown-option-order .select-option .drop dd ul,
:deep(.fixture-order-drop dd ul) {
	right: 0 !important;
	left: auto !important;
}

html[dir="rtl"] .live-bet-dashboard .bet-dashboard-top .dropdown-option-order .select-option .drop dd ul,
html[dir="rtl"] :deep(.fixture-order-drop dd ul) {
	right: auto !important;
	left: 0 !important;
}

@media screen and (min-width: 992px) and (max-width: 1200px) {
	:deep(.original-odds),
	:deep(.arrow-sep) {
		display: none !important;
	}
}

.live-bet-dashboard .bet-dashboard-top .select-option .drop .tabIco {
	pointer-events: none;
}

.live-tab-info .live-info-outer .live-info-inner.date-sub-header,
.date-sub-header {
	background: transparent !important;
	cursor: default !important;

	&:hover {
		background: transparent !important;
		cursor: default !important;
	}

	@media (max-width: 991px) {
		background: transparent !important;
		border-bottom: none !important;
		padding: 10px 0 6px 0 !important;

		.competetion-date .live-match-name h3 {
			font-size: 11px !important;
			font-weight: 300 !important;
			color: var(--white) !important;
			white-space: nowrap !important;
			margin: 0 !important;
			padding: 0 !important;
		}
	}
}

@media screen and (min-width: 320px) and (max-width: 767px) {
	.dropdown-option-top {
		display: block !important;
	}

	.dropdown-option-top-2 {
		display: none !important;
	}

	:deep(.upcomimg_sport),
	:deep(.live-info-inner.upcomimg_sport) {
		padding-left: 0px !important;
		padding-right: 0px !important;
	}
}

/* Date headers styling */
.competetion-date {
	display: block !important;
	width: 100% !important;
}

.competetion-date .live-match-name {
	display: block !important;
	width: 100% !important;
}

.competetion-date .live-match-name h3 {
	font-size: 13px !important;
	color: var(--white) !important;
	padding-right: 10px !important;
	white-space: nowrap !important;
	text-overflow: ellipsis !important;
	overflow: visible !important;
	margin: 0 !important;
}

.time-overview-top {
	margin-bottom: 12px;
	padding-bottom: 8px;
	border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.market-arrow {
	display: flex;
	align-items: center;
	justify-content: center;
	height: 100%;
	cursor: pointer;
	padding: 4px;
}

.market-arrow-icon {
	display: inline-block;
	content: url('@/assets/images/chevron-right.svg');
	filter: invert(65%);
	transform: scale(0.8) rotate(270deg);
	transition: transform 0.25s ease;
	width: 18px;
	height: 18px;
}

.market-arrow-icon-closed {
	display: inline-block;
	content: url('@/assets/images/chevron-right.svg');
	filter: invert(65%);
	transform: scale(0.8) rotate(90deg);
	transition: transform 0.25s ease;
	width: 18px;
	height: 18px;
}

.live-tab-info .live-info-outer {
	border-bottom: none !important;

	@media (max-width: 991px) {
		padding: 0 !important;
		margin: 0 !important;
		border-bottom: none !important;
	}
}

@media (max-width: 991px) {
	.live-tab-info,
	#tabs,
	.tab-content,
	#popular.tab-content,
	#by-time.tab-content,
	.live-bet-dashboard,
	.pre-sports-bet-boosts-container {
		max-height: unset !important;
		height: auto !important;
		overflow: visible !important;
		overflow-y: visible !important;
	}
}

.competition-header-row,
.date-header-row {
	cursor: pointer !important;
	user-select: none;

	* {
		cursor: pointer !important;
	}

	&:hover {
		background: transparent !important;
	}
}

.competition-header-row,
.date-header-row {
	border-bottom: 1px solid var(--tab-gray) !important;

	/* @media (min-width: 992px) {
		&.not-first-competition,
		.live-info-outer:not(:first-child) & {
			border-top: 1px solid var(--tab-gray) !important;
			margin-top: 14px;
		}
	} */

	@media (max-width: 991px) {
		display: flex !important;
		align-items: center !important;
		justify-content: space-between !important;
		width: 100% !important;
		padding: 11px 0 !important;
		margin: 0 !important;
		border-top: none !important;
		border-bottom: 1px solid var(--tab-gray) !important;

		.live-info-outer:first-child & {
			border-top: 1px solid var(--tab-gray) !important;
		}

		.live-info-outer.is-open + .live-info-outer & {
			border-top: 1px solid var(--tab-gray) !important;
			margin-top: 12px !important;
			padding-top: 11px !important;
		}

		.live-info-outer.is-closed + .live-info-outer & {
			border-top: none !important;
			margin-top: 0 !important;
		}

		.live-info-team {
			flex: 1;
			min-width: 0;

			.live-match {
				display: flex !important;
				align-items: center !important;
				gap: 10px !important;
				width: 100% !important;

				.country-flag,
				.team-flag {
					display: flex !important;
					align-items: center !important;
					justify-content: center !important;
					width: 18px !important;
					height: 18px !important;
					min-width: 18px !important;
					overflow: hidden !important;
					border-radius: 50% !important;
					margin-top: 1px !important;
					flex-shrink: 0 !important;

					img {
						width: 18px !important;
						height: 28px !important;
						object-fit: cover !important;
						object-position: center !important;
					}
				}

				.live-match-name {
					flex: 1;
					min-width: 0;

					h3 {
						color: var(--primary) !important;
						font-size: 13px !important;
						font-weight: 700 !important;
						white-space: nowrap !important;
						overflow: hidden !important;
						text-overflow: ellipsis !important;
						margin: 0 !important;
						padding: 0 !important;
					}
				}
			}
		}

		.takis-gkonias {
			display: flex !important;
			align-items: center !important;
			gap: 6px !important;
			padding-left: 8px !important;
			flex-shrink: 0 !important;
			cursor: pointer !important;

			span {
				color: var(--white) !important;
				font-size: 11px !important;
				font-weight: 400 !important;
			}

			.icon {
				width: 0;
				height: 0;
				border-left: 5px solid transparent;
				border-right: 5px solid transparent;
				border-top: 7px solid var(--input-gray);
				border-bottom: 0;
				transition: transform 0.3s ease;

				&.rotated {
					transform: rotate(180deg);
				}
			}
		}

		.full-time-top,
		.match-goal-top {
			display: none !important;
		}
	}
}

.bet-boosts-top-dashboard {
	.tab-pills {
		border-bottom: none !important;
	}

	@media (max-width: 991px) {
		.bet-dashboard-top {
			display: flex !important;
			flex-wrap: wrap !important;
			align-items: center !important;
			justify-content: space-between !important;
			gap: 8px !important;
			padding-bottom: 10px !important;

			&:after {
				display: none !important;
				content: none !important;
			}

			.tabing-dashboard {
				flex: 0 0 100% !important;
				width: 100% !important;
				margin-bottom: 8px !important;
				padding-bottom: 8px !important;
				border-bottom: 1px solid var(--tab-gray) !important;

				.tab-pills {
					display: flex !important;
					align-items: center !important;
					border-bottom: none !important;
					width: 100% !important;
				}

				.boost-tab-pill {
					width: auto !important;
					padding: 4px 0 !important;

					&:after {
						display: none !important;
						content: none !important;
					}
				}
			}

			.dropdown-option-top {
				flex: 0 0 auto !important;
			}

			.dropdown-option-order {
				margin-left: auto !important;
				flex: 0 0 auto !important;
			}
		}
	}
}

.market-arrow-mobile {
	display: none;
}

.icon {
	width: 0;
	height: 0;
	border-left: 5px solid transparent;
	border-right: 5px solid transparent;
	border-top: 7px solid var(--input-gray);
	border-bottom: 0;
	transition: transform 0.3s ease;

	&.rotated {
		transform: rotate(180deg);
	}
}
</style>
